<template>
    <!-- Признак открытой карточки нужен самой раскладке: ширина области меняется без
         изменения ширины окна, а медиазапросы про окно и знают. Под этим классом панель
         фильтров и поля страницы переключаются на шаг раньше — см. стили ниже. -->
    <main class="inventory-page__content" :class="{ 'inventory-page__content--with-drawer': isPanelOpen }">
        <VInventoryHeader></VInventoryHeader>

        <!-- inline: заголовок слева, основное действие страницы справа в той же строке. -->
        <VContentHeaderDefault
            title="Оборудование"
            :button="$_canCreateInventoryItem ? 'Добавить оборудование' : ''"
            hide-search
            inline
            @button="onCreateButtonClick"
        ></VContentHeaderDefault>

        <!-- Поиск, фильтры и действия — одна панель, а не три независимых блока.
             Раньше они были тремя контейнерами с собственным переносом: при нехватке
             ширины вниз уезжал то один фильтр, то кнопка выгрузки, и строка распадалась.
             Здесь перенос задан сеткой и происходит целыми зонами — см. .inventory-toolbar.

             Панель вынесена из-под isLoading вместе с чипами: иначе вся верхняя зона
             мигала бы при каждой перезагрузке списка. Спиннер остаётся вместо таблицы. -->
        <section class="inventory-toolbar">
            <!-- Поиск вне VContentHeaderDefault: его собственное поле ищет без подсказки
                 и хоткея, а нам нужна общая строка поиска Service Desk. -->
            <div class="inventory-toolbar__search">
                <BaseToolsSearch
                    placeholder="Поиск по наименованию, серийному номеру и ответственному"
                    v-model="searchInput"
                    @submit="onSearchSubmit"
                ></BaseToolsSearch>
            </div>

            <div class="inventory-toolbar__filters">
                <div class="inventory-toolbar__filter">
                    <label> Статус: </label>

                    <VSelect
                        class="active-form__control control"
                        placeholder="Любой"
                        multiple
                        clearable
                        label="label"
                        :options="statusOptions"
                        :value="selectedStatusOptions"
                        @input="onStatusFilterSelected"
                    />
                </div>

                <div class="inventory-toolbar__filter">
                    <label> Ответственный: </label>

                    <VSelect
                        class="active-form__control control"
                        placeholder="Любой"
                        multiple
                        clearable
                        label="label"
                        :options="holderOptions"
                        :value="selectedHolderOptions"
                        @input="onHolderFilterSelected"
                    />
                </div>

                <!-- Город и юр. лицо хранят и отправляют ИДЕНТИФИКАТОРЫ справочников.
                     Показывать текст нельзя: у одного юр. лица несколько строк под разными
                     написаниями типа, и по тексту фильтр вёл бы себя непредсказуемо.
                     Пустой справочник не прячем, а гасим: исчезающий фильтр заставляет
                     гадать, куда он делся, а погашенный честно говорит, что выбирать нечего. -->
                <div class="inventory-toolbar__filter">
                    <label> Город: </label>

                    <VSelect
                        class="active-form__control control"
                        :placeholder="cityOptions.length ? 'Любой' : 'Справочник пуст'"
                        multiple
                        clearable
                        label="name"
                        :disabled="!cityOptions.length"
                        :options="cityOptions"
                        :value="selectedCityOptions"
                        @input="onCityFilterSelected"
                    />
                </div>

                <div class="inventory-toolbar__filter">
                    <label> Юр. лицо: </label>

                    <VSelect
                        class="active-form__control control"
                        :placeholder="legalEntityOptions.length ? 'Любое' : 'Справочник пуст'"
                        multiple
                        clearable
                        label="name"
                        :disabled="!legalEntityOptions.length"
                        :options="legalEntityOptions"
                        :value="selectedLegalEntityOptions"
                        @input="onLegalEntityFilterSelected"
                    />
                </div>
            </div>

            <!-- Действия над текущей выборкой, а не над поиском: сброс фильтров и выгрузка
                 того, что сейчас в списке. Поэтому они здесь, а не в строке поиска. -->
            <div class="inventory-toolbar__actions">
                <!-- Ссылка видит всё состояние целиком, включая статусы, просрочки и поиск:
                     раньше кнопка смотрела только на значения атрибутов и не показывалась,
                     хотя сама очистка сбрасывала и статус тоже. -->
                <button type="button" class="inventory-toolbar__reset" v-if="isFiltersDirty" @click="onFiltersClear">
                    Сбросить фильтры
                </button>

                <!-- Выгрузка привязана к своему праву, а не к правке: наблюдатель
                     выгружать может, менять — нет. -->
                <VButton
                    class="button_green-light inventory-toolbar__export"
                    v-if="$_canExportInventory"
                    :disabled="isExporting"
                    @click="exportToExcel"
                >
                    {{ isExporting ? 'Готовим файл…' : 'Выгрузить в Excel' }}
                </VButton>
            </div>
        </section>

        <!-- Чипы статусов — основной способ фильтрации, отдельной строкой под выпадающими
             фильтрами. Клик переключает статус. Просрочки здесь же, отдельными срезами:
             у них своя семантика, но для пользователя это такие же переключатели. -->
        <StatsBar
            bold
            title=""
            class="inventory-page__stats"
            :stats="stats"
            :labels="statusLabels"
            :colors="statusColors"
            :order="statusOrder"
            :visible-statuses="visibleStatuses"
            :extra-items="overdueItems"
            :show-new="false"
            :active-status="filters.state.statuses"
            @filter-by-status="onStatusClick"
        ></StatsBar>

        <template v-if="isLoading">
            <div class="inventory-page__loader">
                <VSpinner></VSpinner>
            </div>
        </template>

        <template v-else>
            <!-- Собственная область прокрутки таблицы. Вертикальная — чтобы шапка, панель
                 фильтров и футер оставались на месте при длинном списке; горизонтальная —
                 чтобы колонки не сжимались ниже читаемой ширины, когда справа открыта
                 карточка. Прокручиваются заголовок и строки вместе: они внутри одного
                 контейнера. -->
            <div class="inventory-page__table">
                <VContentTableDefault
                    dense
                    hide-paginator
                    :rows="rows"
                    :page="page"
                    :total="total"
                    :limit="perPage"
                    :options="options"
                    :selected-id="$route.params.id"
                    @page="onPageChange"
                    @click="onInventoryClick"
                ></VContentTableDefault>

                <!-- Пустая выборка — словами, а не голой строкой заголовков таблицы. -->
                <p class="inventory-page__empty" v-if="!rows.length">Оборудование по заданным условиям не найдено.</p>
            </div>

            <!-- Футер списка обращений: счётчик, выбор числа строк и номера страниц.
                 Текст подставляем слотом — там «N обращений», здесь нужен диапазон. -->
            <RequestStoreFooter
                class="inventory-page__footer"
                v-if="total > 0"
                :total="total"
                :total-pages="totalPages"
                :per-page="perPage"
                :value="page"
                @per-page="onPerPageChange"
                @input="onPageChange"
            >
                <template v-slot:summary> Показано {{ rangeFrom }}–{{ rangeTo }} из {{ total }} </template>
            </RequestStoreFooter>
        </template>

        <VInventoryTemplatePickerModal></VInventoryTemplatePickerModal>
        <VInventoryBatchModal :cache="cache"></VInventoryBatchModal>
        <VInventoryAttributeModal :cache="cache"></VInventoryAttributeModal>
    </main>
</template>
<script>
// Components
import VSpinner from '../../components/VProgress/VSpinner.vue';
import VSelect from '../../components/VSelect/VSelect.vue';
import PagePaginate from '../../components/PagePaginate/PagePaginate.vue';
import VContentTableDefault from '../../components/VContentTableDefault.vue';
import RequestStoreFooter from '../../components/RequestStorePagination/RequestStoreFooter.vue';
import VContentHeaderDefault from '../../components/VContentHeaderDefault.vue';
import VInventoryHeader from './components/VInventoryHeader.vue';
import VInventoryAttributeModal from './modals/VInventoryAttributeModal.vue';
import VInventoryTemplatePickerModal from './modals/VInventoryTemplatePickerModal.vue';
import VInventoryBatchModal from './modals/VInventoryBatchModal.vue';
import './InventoryPage.scss';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { errorMessage } from '../../../js/error';
import { availableCityIds } from '../../../js/inventoryScope';
import axios from 'axios';
import {
    postPaginateInventoryItems,
    getInventoryAttributeTypes,
    getInventorySourceData,
    getAllInventoryUsers,
    paginateInventoryHolders,
    getInventoryItemStats,
    exportInventoryItems,
} from '../../../js/inventory-api';
import { createInventoryFilters, OVERDUE_RETURN, OVERDUE_RESERVE } from '../../../js/inventoryFilters';
import {
    SYSTEM_ATTRIBUTE,
    isSystemAttributeType,
    itemCityName,
    systemAttributeValue,
} from '../../../js/inventoryAttributes';
import { templateDefaults } from '../../../js/inventoryTemplate';
import { Watch } from 'vue-property-decorator';
import ModalNames from '../../../js/enums/ModalNames';
import InventoryItemStatusType from '../../../js/enums/InventoryItemStatusType';
import InventoryItemStatusTitle from '../../../js/enums/InventoryItemStatusTitle';
import InventoryItemStatusColor from '../../../js/enums/InventoryItemStatusColor';
import InventoryHolderTypeTitle from '../../../js/enums/InventoryHolderTypeTitle';
import VButton from '../../components/VButton/VButton.vue';
import BaseToolsSearch from '../../components/tools/BaseToolsSearch.vue';
import StatsBar from '../../components/tools/StatsBar.vue';

// Виды держателя в составном значении фильтра. Зеркало констант контроллера.
const Workspace = namespace('workspace');

const HOLDER_KIND_ACCOUNT = 'account';
const HOLDER_KIND_DICTIONARY = 'holder';

@Component({
    components: {
        VSpinner,
        VSelect,
        PagePaginate,
        VContentTableDefault,
        RequestStoreFooter,
        VContentHeaderDefault,
        VInventoryHeader,
        VInventoryAttributeModal,
        VInventoryTemplatePickerModal,
        VInventoryBatchModal,
        VButton,
        BaseToolsSearch,
        StatsBar,
    },
})
class InventoryPageItems extends Vue {
    @Workspace.Mutation SET_INVENTORY_SCOPE;

    rows = [];
    perPage = 15;

    // Docked-позиция прокрутки списка. Нужна только на телефоне и только из-за того,
    // КАК там открывается карточка: список не размонтируется, а прячется родителем через
    // display: none (см. App.vue). Скрытый элемент теряет раскладку, и вместе с ней
    // браузер обнуляет scrollTop — вернувшись из карточки, пользователь оказывался
    // в начале списка, хотя список всё это время был жив.
    //
    // На широком экране список не прячется и сам не прокручивается (прокручивается
    // таблица внутри), поэтому значение остаётся нулём и восстановление ничего не делает.
    listScrollTop = 0;
    page = 1;
    total = 1;
    isLoading = true;
    searchString = '';
    // «Цены» и «Атрибутов» здесь нет: первой нет в макете, вторая занимала треть ширины
    // и не оставляла места серийному номеру и филиалу. Значения атрибутов целиком видны
    // в карточке и в выгрузке.
    // minWidth — ширина, ниже которой колонка не сжимается. Доли в процентах делят
    // свободное место, но ничего не обещают: с открытой справа карточкой «Серийный номер»
    // ужимался до пары символов, а заголовок и значение начинали слипаться. Теперь вместо
    // этого таблица уезжает в горизонтальную прокрутку и остаётся читаемой.
    //
    // Значения расставлены по приоритету колонок: наименование, ответственный и статус
    // получают больше, даты — минимум, которого хватает на «31.12.2026». Скрывать колонки
    // не стали: в проекте такого приёма нет, а прокрутка честнее исчезающих данных.
    //
    // В одну строку (nowrap) только то, что не переносится по смыслу: номер, серийник
    // и даты. Наименование, ФИО ответственного, юр. лицо и город переносятся — они длинные.
    options = {
        editable: false,
        deletable: false,
        columns: [
            {
                size: 5,
                minWidth: 64,
                className: 'admin-page-table__cell_nowrap',
                label: '№',
                display: (x) => x.id,
            },
            {
                size: 20,
                minWidth: 200,
                label: 'Наименование',
                display: (x) => x.name,
            },
            {
                size: 12,
                minWidth: 140,
                className: 'admin-page-table__cell_nowrap',
                label: 'Серийный номер',
                display: (x) => systemAttributeValue(x, SYSTEM_ATTRIBUTE.SERIAL_NUMBER),
            },
            {
                size: 13,
                minWidth: 128,
                label: 'Статус',
                display: (x) => InventoryItemStatusTitle[x.status] ?? '-',
                badge: (x) => ({
                    label: InventoryItemStatusTitle[x.status] ?? '-',
                    color: InventoryItemStatusColor[x.status] ?? '#9aa0a6',
                }),
            },
            {
                size: 16,
                minWidth: 180,
                label: 'Ответственный',
                display: (x) => this.holderTitle(x),
            },
            {
                size: 12,
                minWidth: 140,
                label: 'Юр. лицо',
                display: (x) => systemAttributeValue(x, SYSTEM_ATTRIBUTE.LEGAL_ENTITY),
            },
            {
                size: 10,
                minWidth: 120,
                label: 'Город',
                display: (x) => itemCityName(x),
            },
            {
                size: 6,
                minWidth: 104,
                className: 'admin-page-table__cell_nowrap',
                label: 'Дата поставки',
                display: (x) => this.$_toLocalDateHistory(x.delivered_at),
            },
            {
                size: 6,
                minWidth: 104,
                className: 'admin-page-table__cell_nowrap',
                label: 'Гарантия',
                display: (x) => this.$_toLocalDateHistory(x.warranty_expire_at),
            },
        ],
    };
    // Пустые коллекции, а не null: справочники доезжают асинхронно, а шаблон читает их
    // с первого рендера. vue-select зовёт options.concat() и на null падает, а обращение
    // cache.attributesMap[...] к null — это TypeError ещё раньше. Прежние значения были
    // видны как ошибка в консоли при каждом открытии страницы и заслоняли собой новые.
    cache = {
        users: [],
        holders: [],
        attributes: [],
        attributesMap: {},
        attributesTypes: [],
        cities: [],
        legalEntities: [],
    };
    // Всё состояние фильтров — в одном объекте: строка счётчиков, селекты и кнопка очистки
    // читают и меняют его, поэтому разойтись им негде.
    filters = createInventoryFilters();
    filterWhereId = [];
    searchInput = '';
    stats = null;
    isExporting = false;
    statsAbortController = null;

    // Список для селекта статусов: значение + подпись.
    statusOptions = Object.keys(InventoryItemStatusTitle).map((value) => ({
        value: Number(value),
        label: InventoryItemStatusTitle[value],
    }));

    statusLabels = InventoryItemStatusTitle;
    statusColors = InventoryItemStatusColor;

    // Порядок пилюль: сначала техника в обороте, выбывшая — в конце.
    statusOrder = [
        InventoryItemStatusType.IN_USE,
        InventoryItemStatusType.IN_STOCK,
        InventoryItemStatusType.RESERVED,
        InventoryItemStatusType.IN_REPAIR,
        InventoryItemStatusType.BROKEN,
        InventoryItemStatusType.DECOMMISSIONED,
    ];

    visibleStatuses = Object.keys(InventoryItemStatusTitle);

    // Просрочки — не значения колонки status, а срезы выборки, поэтому идут отдельными
    // пилюлями. countKey совпадает с ключом ответа /stats и с именем параметра запроса.
    overdueItems = [
        { key: OVERDUE_RETURN, label: 'Просроченный возврат', color: '#e53935', countKey: OVERDUE_RETURN },
        { key: OVERDUE_RESERVE, label: 'Просроченный резерв', color: '#f2994a', countKey: OVERDUE_RESERVE },
    ];

    // Вид ответственного выводится из того, какая ссылка непустая: сотрудник Service Desk лежит
    // в responsible_person, все остальные — в holder. Обе разом заполненными не бывают.
    holderTitle(item) {
        if (item.responsible_person) {
            return item.responsible_person.displayName;
        }

        if (item.holder) {
            const type = InventoryHolderTypeTitle[item.holder.type] ?? item.holder.type;

            return `${item.holder.full_name} (${String(type).toLowerCase()})`;
        }

        return '-';
    }

    get totalPages() {
        return Math.max(1, Math.ceil(this.total / this.perPage));
    }

    get rangeFrom() {
        return this.total === 0 ? 0 : (this.page - 1) * this.perPage + 1;
    }

    get rangeTo() {
        return Math.min(this.page * this.perPage, this.total);
    }

    onPerPageChange(perPage) {
        this.perPage = perPage;
        this.page = 1;
    }

    get isFiltersDirty() {
        return this.filters.isDirty() || this.filterWhereId.length > 0;
    }

    /**
     * Открыта ли карточка справа.
     *
     * Ширина рабочей области зависит от неё так же сильно, как от ширины окна, но
     * медиазапросы про это не знают: окно не меняется. Признак отдаётся раскладке классом.
     */
    get isPanelOpen() {
        return Boolean(this.$route.params.id);
    }

    get selectedStatusOptions() {
        return this.statusOptions.filter((option) => this.filters.state.statuses.indexOf(option.value) !== -1);
    }

    // Держатель живёт в двух взаимоисключающих колонках, поэтому значение составное:
    // один только id указал бы сразу на сотрудника и на запись справочника.
    get holderOptions() {
        return [
            ...this.cache.users.map((user) => ({
                value: `${HOLDER_KIND_ACCOUNT}:${user.id}`,
                kind: HOLDER_KIND_ACCOUNT,
                id: user.id,
                label: user.displayName,
            })),
            ...this.cache.holders.map((holder) => ({
                value: `${HOLDER_KIND_DICTIONARY}:${holder.id}`,
                kind: HOLDER_KIND_DICTIONARY,
                id: holder.id,
                label: `${holder.full_name} (справочник)`,
            })),
        ];
    }

    get selectedHolderOptions() {
        const selected = this.filters.state.holder.map((holder) => `${holder.kind}:${holder.id}`);

        return this.holderOptions.filter((option) => selected.indexOf(option.value) !== -1);
    }

    // ?? [] — справочники доезжают асинхронно, а vue-select зовёт options.concat().
    get cityOptions() {
        return this.cache.cities ?? [];
    }

    get legalEntityOptions() {
        return this.cache.legalEntities ?? [];
    }

    // В состоянии лежат идентификаторы, а селекту нужны сами опции: разворачиваем
    // обратно по id. Опция, которой больше нет в справочнике, просто не подсветится,
    // и фильтр от этого не сломается.
    get selectedCityOptions() {
        return this.cityOptions.filter((option) => this.filters.state.city.indexOf(option.id) !== -1);
    }

    get selectedLegalEntityOptions() {
        return this.legalEntityOptions.filter((option) => this.filters.state.legalEntity.indexOf(option.id) !== -1);
    }

    onCityFilterSelected(options) {
        this.page = 1;
        this.filters.setValues(
            'city',
            (options ?? []).map((option) => option.id),
        );
    }

    onLegalEntityFilterSelected(options) {
        this.page = 1;
        this.filters.setValues(
            'legalEntity',
            (options ?? []).map((option) => option.id),
        );
    }

    onSearchSubmit(search) {
        this.page = 1;
        this.filters.setSearch(search);
    }

    onPageChange(page) {
        this.page = page;
    }

    onStatusClick(status) {
        this.page = 1;

        if (status === null) {
            this.filters.clearStatuses();
            return;
        }

        this.filters.toggleStatus(status);
    }

    onStatusFilterSelected(options) {
        this.page = 1;

        // Селект управляет только статусами; просрочки выбираются пилюлями и обязаны
        // пережить правку селекта, поэтому переносим их из текущего состояния.
        const overdue = this.filters.state.statuses.filter((status) => typeof status === 'string');
        const statuses = (options ?? []).map((option) => option.value);

        this.filters.setValues('statuses', [...statuses, ...overdue]);
    }

    onHolderFilterSelected(options) {
        this.page = 1;
        this.filters.setValues(
            'holder',
            (options ?? []).map((option) => ({ kind: option.kind, id: option.id })),
        );
    }

    onFiltersClear() {
        this.page = 1;
        this.filterWhereId = [];
        this.searchInput = '';
        this.filters.resetAll();
    }

    async exportToExcel() {
        if (this.isExporting) {
            return;
        }

        this.isExporting = true;
        this.$bus.emit('export:progress:start');

        try {
            const response = await exportInventoryItems(this.buildQuery());
            this.downloadExportFile(response);
            this.$bus.emit('export:progress:success');
        } catch (error) {
            this.$bus.emit('export:progress:error', await this.extractExportErrorMessage(error));
        } finally {
            this.isExporting = false;
        }
    }

    // responseType 'blob' означает, что и тело ошибки приезжает блобом: читаем и разбираем,
    // чтобы показать сообщение сервера, а не «не удалось».
    async extractExportErrorMessage(error) {
        const fallback = 'Не удалось сформировать файл. Попробуйте позже.';
        const data = error && error.response ? error.response.data : null;

        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                return parsed.message || fallback;
            } catch (e) {
                return fallback;
            }
        }

        return errorMessage(error) || fallback;
    }

    downloadExportFile(response) {
        const blob = new Blob([response.data], {
            type:
                response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = this.exportFileName(response.headers['content-disposition']);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    exportFileName(contentDisposition) {
        const fallback = 'inventory-export.xlsx';

        if (!contentDisposition) {
            return fallback;
        }

        const utfName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

        if (utfName && utfName[1]) {
            return decodeURIComponent(utfName[1]);
        }

        const plainName = contentDisposition.match(/filename="?([^";]+)"?/i);

        return plainName && plainName[1] ? plainName[1] : fallback;
    }

    // Карточка открывается боковой панелью: это переход по тому же маршруту с добавленным
    // параметром, поэтому список не перемонтируется и остаётся на своей странице и фильтрах.
    onInventoryClick(item) {
        if (String(this.$route.params.id) === String(item.id)) {
            return;
        }

        this.$router.push({ name: 'inventory.items', params: { id: String(item.id) } });
    }

    async onCreateButtonClick() {
        // Вторая застава помимо скрытой кнопки: права мало — нужен ещё хотя бы один город,
        // в который пользователю разрешено писать. Иначе форма открылась бы ради отказа.
        if (!this.$_canCreateInventoryItem) {
            return;
        }

        let choice;

        try {
            // ШАГ 1: что заводим и сколько. Два вопроса на одном экране — от количества
            // зависит, какая форма откроется дальше, и спрашивать его после общих полей
            // значило бы менять форму под человеком.
            choice = await this.$showModal(ModalNames.INVENTORY_TEMPLATE_PICKER);
        } catch (error) {
            // The modal was closed.
            return;
        }

        const count = Number(choice?.count) > 0 ? Number(choice.count) : 1;
        const defaults = templateDefaults(choice?.preset, (type) => this.systemKeyOf(type));

        await this.createEquipment(defaults, count);
    }

    /**
     * Форма создания — одна на любое количество.
     *
     * ДВУХ ФОРМ СОЗДАНИЯ БОЛЬШЕ НЕТ. До 7-D1 одна единица открывала старую карточку
     * на vee-validate, а партия — свою форму. Та карточка и показывала человеку
     * «validation.required»: русских сообщений в проекте нет, и vee-validate выводил ключ
     * вместо текста. Держать две формы одних и тех же данных было нечем оправдать —
     * они уже начали расходиться составом обязательных полей.
     *
     * Здесь форма партии с одним полем серийного номера при количестве 1.
     *
     * Шаблон подставляет только своё: модель, статус, срок гарантии, сценарий владения
     * и значения справочников. Цена, дата поставки, город и серийный номер остаются
     * пустыми — это свойства конкретной поставки (см. js/inventoryTemplate.js).
     *
     * @param {Object} defaults
     * @param {number} count
     */
    async createEquipment(defaults, count) {
        let created;

        try {
            // Форма сама отправляет запрос и закрывается только после успеха: отказ
            // сервера должен приходить в те поля, которые надо исправить, а не поверх
            // уже закрытой формы (см. VInventoryBatchModal).
            created = await this.$showModal(ModalNames.INVENTORY_BATCH_CREATE, {
                count,
                serialAttribute: this.serialAttributeType,
                common: {
                    // Шаблон, по которому заводится партия. В предмет он не пишется:
                    // нужен серверу, чтобы проверить обязательные поля шаблона самому.
                    preset_id: defaults.preset_id,
                    name: defaults.name,
                    city_id: null,
                    legal_entity_id: null,
                    cost: 0,
                    delivered_at: '',
                    warranty_months: defaults.warranty_months,
                    status: defaults.status,
                    owner_type: defaults.owner_type,
                    responsible_person_id: null,
                    holder_id: null,
                    // Поля формы, собранные из шаблона: подпись, значение по умолчанию
                    // и обязательность. Копия, а не ссылка: форма правит их на месте,
                    // и без копии повторное открытие показывало бы прошлый ввод.
                    fields: defaults.fields.map((field) => ({ ...field })),
                    legal_entity_default: defaults.legal_entity_default,
                },
            });
        } catch (error) {
            // The modal was closed.
            return;
        }

        await this.showCreated(created);
    }

    /**
     * Показать в списке то, что только что завели.
     *
     * @param {Array} created Позиции, вернувшиеся из bulk-insert.
     */
    async showCreated(created) {
        const items = Array.isArray(created) ? created : [];

        if (items.length === 0) {
            return;
        }

        this.isLoading = true;

        try {
            this.filterWhereId = items.map((item) => item.id);
            await this.fetchInventoryItems({ whereId: this.filterWhereId });
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Системный смысл типа атрибута или null.
     *
     * Названия справочников заводит администратор, поэтому решение принимается здесь,
     * а не в модуле: тот не должен знать, как в этой системе называется «Юр. лицо».
     * Порядок проверок не важен — один тип не может значить двух разных вещей.
     *
     * @param {string} type
     * @returns {string|null}
     */
    systemKeyOf(type) {
        const keys = [SYSTEM_ATTRIBUTE.SERIAL_NUMBER, SYSTEM_ATTRIBUTE.LOCATION_CITY, SYSTEM_ATTRIBUTE.LEGAL_ENTITY];

        return keys.find((key) => isSystemAttributeType(type, key)) ?? null;
    }

    /**
     * Справочник, в который кладётся серийный номер.
     *
     * Берётся из загруженных типов атрибутов, а не задаётся строкой: название справочника
     * администратор меняет из интерфейса, и зашитое здесь «Серийный номер» разошлось бы
     * с ним при первом же переименовании.
     */
    get serialAttributeType() {
        const types = this.cache.attributes ?? [];
        const found = types.find((type) => isSystemAttributeType(type.type, SYSTEM_ATTRIBUTE.SERIAL_NUMBER));

        return found ? { type: found.type, value_type: 'string' } : { type: 'Серийный номер', value_type: 'string' };
    }

    // Один наблюдатель на всё состояние фильтров: следить за каждым полем по отдельности
    // значило бы дописывать сюда строку при каждом новом фильтре и однажды забыть.
    @Watch('page')
    @Watch('perPage')
    @Watch('filters.state', { deep: true })
    async watcher() {
        this.isLoading = true;

        try {
            await Promise.all([this.fetchInventoryItems(), this.fetchStats()]);
        } finally {
            this.isLoading = false;
        }
    }

    async fetchRequiredData() {
        this.isLoading = true;

        try {
            await Promise.all([
                this.fetchInventoryItems(),
                this.fetchStats(),
                this.fetchAttributeTypes(),
                this.fetchUsers(),
                this.fetchHolders(),
                this.fetchSourceData(),
            ]);
        } finally {
            // Карточку по адресу открывать отсюда больше не нужно: панель сама читает
            // :item из маршрута и грузит предмет независимо от того, попал ли он в список.
            this.isLoading = false;
        }
    }

    async fetchUsers() {
        try {
            const response = await getAllInventoryUsers(1, '', 100000);
            this.cache.users = response.data.data.map((user) => ({
                id: user.id,
                displayName: user.enabled ? user.displayName : user.displayName + ' (откл.)',
            }));
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    // Один запрос на оба справочника раздела: города нужны и форме создания,
    // и фильтру, юр. лица — фильтру. Источник — контракт модуля
    // /inventory/source-data, а не общий /v1/cities.
    //
    // Зовётся один раз при инициализации страницы. Справочники за время работы
    // со списком не меняются, и перезапрашивать их на каждое переключение фильтра
    // значило бы гонять один и тот же ответ десятки раз.
    async fetchSourceData() {
        try {
            const response = await getInventorySourceData();

            this.cache.cities = response.data.cities ?? [];
            this.cache.legalEntities = response.data.legal_entities ?? [];

            // Область приходит тем же ответом — отдельного запроса ради неё нет.
            this.SET_INVENTORY_SCOPE(response.data.scope ?? null);

            this.dropUnavailableCityFilter();
        } catch (error) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
        }
    }

    /**
     * Убрать из сохранённого фильтра города, которых больше нет в справочнике раздела.
     *
     * Фильтр переживает перезагрузку в localStorage, а состав городских групп меняется
     * в AD. Без очистки в чипах остаётся пункт без названия, а в каждый запрос уходит
     * город, которого пользователь всё равно не увидит, — и список выглядит пустым
     * по непонятной причине.
     *
     * Второй раз по области здесь не фильтруем: справочник уже сужен бэкендом по области
     * просмотра, и он же — единственный источник допустимых значений.
     */
    dropUnavailableCityFilter() {
        const selected = this.filters.state.city;
        const available = availableCityIds(selected, this.cache.cities);

        if (available.length !== selected.length) {
            this.filters.setValues('city', available);
        }
    }

    async fetchHolders() {
        try {
            // Забираем и погашенные: за ними может числиться техника, и в карточке такого
            // держателя нужно показать, а не потерять. Погашенные помечены в самом селекте.
            const response = await paginateInventoryHolders(1, '', { per_page: 100000 });
            this.cache.holders = response.data.data;
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    // Тело запроса собирается в одном месте — списком, счётчиками и выгрузкой,
    // ровно как на бэкенде они строятся одним методом.
    buildQuery(extra = {}) {
        // Открытая карточка список больше не сужает: раньше по /items/:id/card страница
        // показывала один предмет и открывала модалку, теперь рядом с полным списком
        // открывается панель, и whereId здесь только помешал бы.
        return { ...this.filters.buildQuery(), ...extra };
    }

    async fetchInventoryItems(extra = {}) {
        try {
            const response = await postPaginateInventoryItems(this.page, '', this.perPage, this.buildQuery(extra));

            this.rows = response.data.data;
            this.total = response.data.total;
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    async fetchStats() {
        // Счётчики идут вторым запросом параллельно списку, и при быстром переключении
        // фильтров ответы приходят вразнобой — отменяем предыдущий, как это сделано
        // в списке обращений.
        if (this.statsAbortController) {
            this.statsAbortController.abort();
        }

        this.statsAbortController = new AbortController();

        try {
            const response = await getInventoryItemStats(this.buildQuery(), {
                signal: this.statsAbortController.signal,
            });
            this.stats = response.data;
        } catch (error) {
            if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') {
                return;
            }

            // Ошибку счётчиков модалкой не показываем: список при этом работает,
            // и заслонять его из-за строки с цифрами не за что.
            console.warn(error);
        }
    }

    async fetchAttributeTypes() {
        try {
            const result = await getInventoryAttributeTypes({
                withAttributes: true,
            });

            this.cache.attributes = result.data.data;
            this.cache.attributesMap = this.cache.attributes.reduce((carry, attributeType) => {
                carry[attributeType.type] = attributeType;
                return carry;
            }, {});
            this.cache.attributesTypes = this.cache.attributes.map((attribute) => ({
                label: attribute.type,
                value: attribute.type,
            }));
        } catch (error) {
            const message = errorMessage(error);

            this.$showModal(ModalNames.ERROR, { errorMessage: message });
        }
    }

    // Панель правит предмет у себя и о списке не знает — строку обновляем по её сигналу,
    // иначе в таблице остались бы прежние держатель и статус до перезагрузки страницы.
    async onItemChanged() {
        await Promise.all([this.fetchInventoryItems(), this.fetchStats()]);
    }

    /**
     * Запомнить, докуда долистали.
     *
     * Пишется на каждый кадр прокрутки, а не в момент открытия карточки: к моменту,
     * когда о карточке узнаёт этот компонент, родитель может уже применить display: none,
     * и читать scrollTop будет поздно — он к тому времени нуль. Присваивание числа
     * стоит дешевле, чем попытка угадать порядок обновления двух компонентов.
     */
    onListScroll() {
        this.listScrollTop = this.$el ? this.$el.scrollTop : 0;
    }

    /**
     * Карточка закрылась — возвращаем список туда, где его оставили.
     *
     * $nextTick обязателен: родитель снимает display: none тем же циклом обновления,
     * а задать scrollTop скрытому элементу нельзя — значение просто не применится.
     *
     * @param {string|undefined} id
     */
    @Watch('$route.params.id')
    onOpenedItemChanged(id) {
        if (id || !this.listScrollTop) {
            return;
        }

        this.$nextTick(() => {
            if (this.$el) {
                this.$el.scrollTop = this.listScrollTop;
            }
        });
    }

    created() {
        // Состояние фильтров восстанавливается из localStorage самой фабрикой,
        // отдельных загрузчиков под каждый ключ больше не нужно.
        this.fetchRequiredData();
        this.$bus.on('inventory:item-changed', this.onItemChanged);
    }

    mounted() {
        // passive: слушатель ничего не отменяет, и без этого признака браузер на телефоне
        // ждёт его решения перед каждым кадром прокрутки.
        this.$el.addEventListener('scroll', this.onListScroll, { passive: true });
    }

    beforeDestroy() {
        this.$el.removeEventListener('scroll', this.onListScroll);
        this.$bus.off('inventory:item-changed', this.onItemChanged);

        if (this.statsAbortController) {
            this.statsAbortController.abort();
        }
    }
}

export default InventoryPageItems;
</script>
<style lang="scss">
// Раскладка списка оборудования.
//
// ПОЧЕМУ СЕТКА, А НЕ flex-wrap. Раньше поиск, фильтры и действия были тремя контейнерами
// с собственным переносом, и при нехватке ширины каждый переносился сам по себе: вниз
// уезжал то один фильтр из четырёх, то кнопка выгрузки, а justify-content: space-between
// растаскивал остатки по краям. Здесь перенос описан явно и происходит целыми зонами:
// либо всё в строку, либо «поиск + действия» и фильтры под ними, либо три строки.
//
// ПОЧЕМУ КЛАСС, А НЕ МЕДИАЗАПРОС. Ширину рабочей области меняет не только окно, но и
// открытая справа карточка: при её появлении область становится уже на 30–40%, а окно —
// нет. Контейнерных запросов в этом стеке нет, поэтому страница сама сообщает раскладке
// о карточке классом --with-drawer, и панель переключается на шаг раньше.
@mixin inventory-toolbar-two-rows {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
        'search actions'
        'filters filters';
}

@mixin inventory-toolbar-stacked {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
        'search'
        'filters'
        'actions';

    .inventory-toolbar__filters {
        grid-template-columns: repeat(2, minmax(9rem, 1fr));
    }

    .inventory-toolbar__actions {
        justify-content: flex-end;
    }
}

.inventory-toolbar {
    display: grid;
    // Поиск фиксированной разумной ширины, фильтры делят остаток поровну, действия —
    // по содержимому: кнопка выгрузки не сжимается и остаётся в своей зоне справа.
    grid-template-columns: minmax(15rem, 20rem) minmax(0, 1fr) auto;
    grid-template-areas: 'search filters actions';
    align-items: end;
    gap: 12px 16px;
    margin: 0 var(--inventory-gutter) 12px var(--inventory-gutter);
    padding: 14px 16px;
    background: var(--color-white);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    &__search {
        grid-area: search;
        // min-width: 0 у элемента сетки обязателен: без него содержимое задаёт
        // минимальный размер колонки, и поле поиска перестаёт сжиматься вовсе.
        min-width: 0;
    }

    &__filters {
        grid-area: filters;
        display: grid;
        // Четыре фильтра переносятся только вместе: поодиночке уезжать им некуда.
        grid-template-columns: repeat(4, minmax(9rem, 1fr));
        gap: 12px;
        min-width: 0;
    }

    &__filter {
        min-width: 0;

        & label {
            display: block;
            margin-bottom: 4px;
            font-size: 0.8125rem;
            color: #808080;
            white-space: nowrap;
        }
    }

    &__actions {
        grid-area: actions;
        display: flex;
        align-items: center;
        gap: 16px;
        white-space: nowrap;
    }

    // Вторичное действие: текстовая ссылка, а не вторая крупная кнопка рядом с выгрузкой.
    &__reset {
        padding: 0;
        border: 0;
        background: none;
        color: var(--color-green);
        font-size: 0.875rem;
        white-space: nowrap;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    &__export {
        flex-shrink: 0;
        white-space: nowrap;
    }
}

// $media-xl / $media-lg — те же границы, что уже приняты в проекте (scss/_variables.scss).
@media (max-width: $media-xl) {
    .inventory-toolbar {
        @include inventory-toolbar-two-rows;
    }
}

@media (max-width: $media-lg) {
    .inventory-toolbar {
        @include inventory-toolbar-stacked;
    }
}

.inventory-page {
    &__content {
        // Поля страницы. С открытой карточкой они уже: 40 лишних точек по краям — это
        // почти половина колонки «Город».
        --inventory-gutter: 36px;

        // Колонка раскладки: шапка, панель, чипы и футер держат высоту по содержимому,
        // таблица забирает остаток и прокручивается сама. min-height: 0 обязателен —
        // без него flex-элемент не сжимается ниже содержимого, и прокрутка достаётся
        // всей странице целиком вместе с шапкой.
        min-height: 0;

        &--with-drawer {
            --inventory-gutter: 16px;

            .inventory-toolbar {
                @include inventory-toolbar-two-rows;
            }

            @media (max-width: $media-xl) {
                .inventory-toolbar {
                    @include inventory-toolbar-stacked;
                }
            }
        }
    }

    // Строка чипов — отдельный ряд между панелью и таблицей, на фоне страницы, а не
    // внутри белой панели: так она читается как быстрые фильтры, а не как продолжение
    // выпадающих. Высоту не наращиваем — чипы и так однострочные.
    &__stats.stats-bar {
        margin: 0 var(--inventory-gutter) 8px var(--inventory-gutter);
        padding: 0;
    }

    // Область прокрутки таблицы: вертикальная — чтобы шапка и панель фильтров оставались
    // на месте, горизонтальная — чтобы колонки не сжимались ниже minWidth.
    &__table {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
    }

    // Таблица больше не занимает всю оставшуюся высоту: за высоту теперь отвечает
    // контейнер прокрутки выше, а под таблицей стоит футер со счётчиком и пейджером.
    & .admin-page-table {
        height: auto;
        min-height: 0;
        overflow: visible;

        // Тело таблицы — VScroll со своим overflow: auto. Собственная прокрутка ему здесь
        // мешает: строки уезжали бы внутри него, а заголовок остался бы на месте — он
        // лежит выше и в этот контейнер не входит. За обе оси теперь отвечает один
        // внешний контейнер .inventory-page__table, поэтому здесь переполнение видимое.
        .admin-page-table__body {
            height: auto !important;
            padding-bottom: 0;
            overflow: visible !important;
        }

        // Рельса собственного бегунка VScroll без прокрутки не нужна и только ловит место.
        .admin-page-table__body > .v-scroll__rail {
            display: none;
        }

        // Заголовок и строки — один и тот же ряд колонок, поэтому и минимальная ширина
        // у них общая: min-content равен сумме minWidth колонок. Без этого при прокрутке
        // заголовок остался бы по ширине контейнера и разъехался бы со значениями.
        .admin-page-table__head,
        .admin-page-table__row {
            min-width: min-content;
            margin: 0 var(--inventory-gutter);
        }

        // Заголовок остаётся виден при вертикальной прокрутке списка и уезжает вместе
        // со строками при горизонтальной — он в том же контейнере, что и они.
        .admin-page-table__head {
            position: sticky;
            top: 0;
            z-index: $layer-sticky-head;
        }

        // Воздух между колонками. Через padding, а не gap: gap прибавился бы к сумме
        // процентных долей и дал бы горизонтальную прокрутку даже на широком экране.
        .admin-page-table__cell {
            padding-right: 12px;

            &:last-child {
                padding-right: 0;
            }
        }
    }

    &__footer {
        margin: 0 var(--inventory-gutter);
    }
}
</style>
